import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Container,
  Paper,
  ThemeIcon,
  Group,
  Stack,
  TextInput,
  Button,
  Select,
  Badge,
  Table,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Modal,
  Divider,
  Card,
  SimpleGrid,
  ScrollArea,
  Box,
  rem,
  Pagination,
  Textarea,
  NumberInput,
  Radio,
  FileInput,
  UnstyledButton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconReportAnalytics,
  IconSearch,
  IconDownload,
  IconFilter,
  IconEye,
  IconFileText,
  IconArrowLeft,
  IconX,
  IconCalendar,
  IconMapPin,
  IconRuler,
  IconTruckDelivery,
  IconClipboardText,
  IconBuildingFactory,
  IconFileTypePdf,
  IconReceipt2,
  IconDatabase,
  IconPencil,
  IconTrash,
  IconDeviceFloppy,
  IconUpload,
  IconSelector,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const heroGradient =
  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

function formatVolume(value) {
  const num = Number(value || 0);
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return '-';
  const date = parseDateValue(value);
  if (!date) return String(value);
  return date.toLocaleDateString('tr-TR');
}

function parseDateValue(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, d] = match;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateForApi(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDeliveryLabel(type) {
  if (type === 'ic_santiye') return 'İç Şantiye';
  if (type === 'dis_satis') return 'Dış Satış';
  return type || '-';
}

function normalizeText(value) {
  return String(value ?? '').toLowerCase().trim();
}

function getRowTitle(row) {
  return row?.waybillNo || row?.irsaliye_no || row?.waybill_no || '-';
}

function getRowLocation(row) {
  return row?.locationOrCompany || row?.siteName || row?.companyName || row?.location_or_company || row?.destination || '-';
}

function getRowConcrete(row) {
  return row?.concreteType || row?.concrete_type || row?.concrete || '-';
}

function getRowVolume(row) {
  return row?.volume ?? row?.m3 ?? 0;
}

function getRowDate(row) {
  return row?.date || row?.createdAt || row?.created_at || '-';
}

function getRowDeliveryType(row) {
  return row?.deliveryType || row?.delivery_type || '-';
}

function getRowInvoiceNo(row) {
  return row?.invoiceNo || row?.invoice_no || '-';
}

function getRowInvoiceDate(row) {
  return row?.invoiceDate || row?.invoice_date || '-';
}

function safeArray(data) {
  return Array.isArray(data) ? data : [];
}

function toStringOrEmpty(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

const emptyEditForm = {
  id: null,
  waybillNo: '',
  date: null,
  volume: '',
  deliveryType: 'ic_santiye',
  concreteTypeId: '',
  siteId: '',
  companyId: '',
  invoiceNo: '',
  invoiceDate: null,
  description: '',
  waybillFile: null,
  invoiceFile: null,
};

export default function ConcreteReports() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);

  const [data, setData] = useState([]);
  const [options, setOptions] = useState({
    concreteTypes: [],
    sites: [],
    companies: [],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [concreteFilter, setConcreteFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); 

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportRes, optionsRes] = await Promise.all([
        api.get('/records/concrete/all'), 
        api.get('/records/concrete/options'),
      ]);

      setData(safeArray(reportRes.data));

      setOptions({
        concreteTypes: safeArray(optionsRes.data?.concreteTypes),
        sites: safeArray(optionsRes.data?.sites),
        companies: safeArray(optionsRes.data?.companies),
      });
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Rapor verileri yüklenemedi.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, concreteFilter, locationFilter, dateRange, sortConfig]);

  const concreteTypesFromData = useMemo(() => {
    if (options.concreteTypes.length > 0) return options.concreteTypes;
    const map = new Map();

    data.forEach((item) => {
      const id = item?.concreteTypeId;
      const name = getRowConcrete(item);
      if (id !== null && id !== undefined && name && !map.has(String(id))) {
        map.set(String(id), name);
      }
    });

    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [options.concreteTypes, data]);

  const sitesFromData = useMemo(() => {
    if (options.sites.length > 0) return options.sites;
    const map = new Map();

    data.forEach((item) => {
      if (item?.siteId && item?.siteName && !map.has(String(item.siteId))) {
        map.set(String(item.siteId), item.siteName);
      }
    });

    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [options.sites, data]);

  const companiesFromData = useMemo(() => {
    if (options.companies.length > 0) return options.companies;
    const map = new Map();

    data.forEach((item) => {
      if (item?.companyId && item?.companyName && !map.has(String(item.companyId))) {
        map.set(String(item.companyId), item.companyName);
      }
    });

    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [options.companies, data]);

  const locationTypes = useMemo(() => {
    const map = new Map();

    data.forEach((item) => {
      const deliveryType = getRowDeliveryType(item);
      if (deliveryType === 'ic_santiye' && item?.siteId) {
        const value = `site:${item.siteId}`;
        const label = item?.siteName || getRowLocation(item);
        if (label && !map.has(value)) map.set(value, label);
      }
      if (deliveryType === 'dis_satis' && item?.companyId) {
        const value = `company:${item.companyId}`;
        const label = item?.companyName || getRowLocation(item);
        if (label && !map.has(value)) map.set(value, label);
      }
    });

    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
  }, [data]);

  const filteredData = useMemo(() => {
    const q = normalizeText(searchTerm);

    const start = dateRange?.[0] ? new Date(dateRange[0]) : null;
    const end = dateRange?.[1] ? new Date(dateRange[1]) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    return data.filter((item) => {
      const waybillNo = normalizeText(getRowTitle(item));
      const locationOrCompany = normalizeText(getRowLocation(item));
      const concreteType = normalizeText(getRowConcrete(item));
      const deliveryType = getRowDeliveryType(item);

      const matchSearch =
        !q ||
        waybillNo.includes(q) ||
        locationOrCompany.includes(q) ||
        concreteType.includes(q);

      const matchType = typeFilter ? deliveryType === typeFilter : true;
      const matchConcrete = concreteFilter ? String(item?.concreteTypeId) === concreteFilter : true;

      const matchLocation = locationFilter
        ? (deliveryType === 'ic_santiye' && `site:${item?.siteId}` === locationFilter) ||
          (deliveryType === 'dis_satis' && `company:${item?.companyId}` === locationFilter)
        : true;

      const itemDate = parseDateValue(getRowDate(item));
      const matchDate =
        (!start && !end) ||
        (itemDate &&
          (!start || itemDate >= start) &&
          (!end || itemDate <= end));

      return matchSearch && matchType && matchConcrete && matchLocation && matchDate;
    });
  }, [searchTerm, typeFilter, concreteFilter, locationFilter, dateRange, data]);

  const sortedData = useMemo(() => {
    let sortableItems = [...filteredData];
    
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'waybillNo') {
          aValue = normalizeText(getRowTitle(a));
          bValue = normalizeText(getRowTitle(b));
        } else if (sortConfig.key === 'date') {
          aValue = parseDateValue(getRowDate(a))?.getTime() || 0;
          bValue = parseDateValue(getRowDate(b))?.getTime() || 0;
        } else if (sortConfig.key === 'volume') {
          aValue = Number(getRowVolume(a) || 0);
          bValue = Number(getRowVolume(b) || 0);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableItems;
  }, [filteredData, sortConfig]);

  const totalVolume = useMemo(() => {
    return filteredData.reduce((acc, curr) => acc + Number(getRowVolume(curr) || 0), 0);
  }, [filteredData]);

  const totalCount = filteredData.length;

  const deliveryCount = useMemo(() => {
    return filteredData.reduce(
      (acc, row) => {
        const type = getRowDeliveryType(row);
        if (type === 'ic_santiye') acc.internal += 1;
        if (type === 'dis_satis') acc.external += 1;
        return acc;
      },
      { internal: 0, external: 0 }
    );
  }, [filteredData]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize); 
  }, [sortedData, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const ThSortable = ({ children, sortKey }) => {
    const isSorted = sortConfig.key === sortKey;
    const Icon = isSorted ? (sortConfig.direction === 'asc' ? IconChevronUp : IconChevronDown) : IconSelector;
    return (
      <Table.Th style={{ padding: 0 }}>
        <UnstyledButton 
          onClick={() => handleSort(sortKey)} 
          p="xs" 
          w="100%" 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
        >
          <Text fw={700} size="sm">{children}</Text>
          <Icon size={14} stroke={1.5} color={isSorted ? 'var(--mantine-color-blue-filled)' : 'gray'} />
        </UnstyledButton>
      </Table.Th>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setConcreteFilter('');
    setLocationFilter('');
    setDateRange([null, null]);
    setSortConfig({ key: null, direction: 'asc' });
  };

  const openDetailModal = (row) => {
    setSelectedRow(row);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedRow(null);
  };

  const openEditModal = (row) => {
    const deliveryType = row?.deliveryType ?? row?.delivery_type ?? 'ic_santiye';

    setEditForm({
      id: row?.id ?? null,
      waybillNo: toStringOrEmpty(row?.waybillNo ?? row?.waybill_no),
      date: parseDateValue(row?.date),
      volume: toStringOrEmpty(row?.volume ?? ''),
      deliveryType,
      concreteTypeId: toStringOrEmpty(row?.concreteTypeId ?? row?.concrete_type_id),
      siteId: toStringOrEmpty(row?.siteId ?? row?.site_id),
      companyId: toStringOrEmpty(row?.companyId ?? row?.company_id),
      invoiceNo: toStringOrEmpty(row?.invoiceNo ?? row?.invoice_no),
      invoiceDate: parseDateValue(row?.invoiceDate ?? row?.invoice_date),
      description: toStringOrEmpty(row?.description),
      waybillFile: null,
      invoiceFile: null,
    });
    setSelectedRow(row); // Düzenleme modalında PDF durumlarını göstermek için selectedRow'u set ediyoruz.
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditForm(emptyEditForm);
  };

  const openDeleteModal = (row) => {
    setDeleteTarget(row);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const downloadPdf = async (url, fileName) => {
    if (!url) return;

    try {
      const response = await api.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      notifications.show({
        title: 'Sistem Hatası',
        message: 'PDF indirilemedi. Bağlantı veya yetki kontrol edin.',
        color: 'red',
      });
    }
  };

  // Dosya ismini sabit veya response üstbilgisinden güvenle belirleyerek indir.
  const saveXlsxResponse = (response, fallbackName) => {
    const contentType = response.headers?.['content-type'] || '';
    if (contentType && !contentType.includes('spreadsheet') && !contentType.includes('octet-stream')) {
      throw new Error('Sunucu Excel dosyası döndürmedi.');
    }
    const disposition = response.headers?.['content-disposition'] || '';
    const extracted = disposition.match(/filename\*?=(?:UTF-8''|\")?([^;\"]+)/i);
    const serverName = extracted?.[1] ? decodeURIComponent(extracted[1].replace(/"/g, '').trim()) : '';
    const fileName = serverName && /^[a-zA-Z0-9_.-]+\.xlsx$/i.test(serverName) ? serverName : fallbackName;
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  };

  const getExcelError = async (err, fallback) => {
    let detail = err?.response?.data?.detail;
    if (err?.response?.data instanceof Blob) {
      try {
        const payload = JSON.parse(await err.response.data.text());
        detail = payload?.detail || payload?.message;
      } catch { /* Excel cevabı olmayan bir hata olabilir. */ }
    }
    return typeof detail === 'string' ? detail : (err?.message || fallback);
  };

  const handleExportExcel = async () => {
    if (sortedData.length === 0) {
      notifications.show({ title: 'Bilgi', message: 'Dışa aktarılacak kayıt yok.', color: 'blue' });
      return;
    }
    const ids = sortedData.map((row) => Number(row.id)).filter((id) => Number.isInteger(id) && id > 0);
    if (ids.length !== sortedData.length) {
      notifications.show({ title: 'Hata', message: 'Kayıt numaraları eksik; raporu yenileyip tekrar deneyin.', color: 'red' });
      return;
    }
    const filterDescription = [
      searchTerm.trim() && `Arama: ${searchTerm.trim()}`,
      typeFilter && `Teslimat: ${getDeliveryLabel(typeFilter)}`,
      concreteFilter && `Beton: ${concreteTypesFromData.find((item) => item.value === concreteFilter)?.label || concreteFilter}`,
      locationFilter && `Konum: ${locationTypes.find((item) => item.value === locationFilter)?.label || locationFilter}`,
      (dateRange?.[0] || dateRange?.[1]) && `Tarih: ${dateRange?.[0] ? formatDate(dateRange[0]) : 'Başlangıç yok'} - ${dateRange?.[1] ? formatDate(dateRange[1]) : 'Bitiş yok'}`,
    ].filter(Boolean).join(' | ') || 'Tüm kayıtlar';

    setExporting(true);
    try {
      const response = await api.post(
        '/records/concrete/export-report',
        { record_ids: ids, filter_summary: filterDescription },
        { responseType: 'blob' },
      );
      saveXlsxResponse(response, `SMS_Grup_Beton_Raporu_${formatDateForApi(new Date())}.xlsx`);
      notifications.show({
        title: 'Excel hazır',
        message: `${ids.length} kayıt, seçili filtreler ve sıralama ile tasarımlı olarak indirildi.`,
        color: 'teal',
      });
    } catch (err) {
      notifications.show({
        title: 'Excel indirilemedi',
        message: await getExcelError(err, 'Sunucu raporu oluşturamadı.'),
        color: 'red',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateDownloading(true);
    try {
      const response = await api.get('/records/concrete/export-template', {
        responseType: 'blob',
        params: { _download: Date.now() },
      });
      saveXlsxResponse(response, 'Beton_Irsaliye_Sablonu.xlsx');
      notifications.show({
        title: 'Şablon indirildi',
        message: 'Beton irsaliye veri giriş şablonu hazır.',
        color: 'teal',
      });
    } catch (err) {
      notifications.show({
        title: 'Şablon indirilemedi',
        message: await getExcelError(err, 'Şablon oluşturulamadı.'),
        color: 'red',
      });
    } finally {
      setTemplateDownloading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editForm.id) return;

    if (!editForm.date || editForm.volume === '' || !editForm.concreteTypeId) {
      notifications.show({
        title: 'Eksik Alan',
        message: 'Tarih, hacim ve beton cinsi zorunludur.',
        color: 'red',
      });
      return;
    }

    if (editForm.deliveryType === 'ic_santiye' && !editForm.siteId) {
      notifications.show({
        title: 'Eksik Alan',
        message: 'İç şantiye için şantiye seçmelisin.',
        color: 'red',
      });
      return;
    }

    if (editForm.deliveryType === 'dis_satis' && !editForm.companyId) {
      notifications.show({
        title: 'Eksik Alan',
        message: 'Dış satış için firma seçmelisin.',
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      
      const formattedDate = formatDateForApi(editForm.date);
      if (formattedDate) formData.append('date', formattedDate);
      
      const formattedInvoiceDate = formatDateForApi(editForm.invoiceDate);
      if (formattedInvoiceDate) formData.append('invoice_date', formattedInvoiceDate);
      
      formData.append('volume', Number(editForm.volume));
      formData.append('delivery_type', editForm.deliveryType);
      
      const concreteId = toNumberOrNull(editForm.concreteTypeId);
      if (concreteId) formData.append('concrete_type_id', concreteId);
      
      if (editForm.deliveryType === 'ic_santiye') {
        const siteId = toNumberOrNull(editForm.siteId);
        if (siteId) formData.append('site_id', siteId);
      }
      
      if (editForm.deliveryType === 'dis_satis') {
        const companyId = toNumberOrNull(editForm.companyId);
        if (companyId) formData.append('company_id', companyId);
      }

      // Fatura No iç ve dış şantiye fark etmeksizin varsa ekleniyor
      if (editForm.invoiceNo?.trim()) {
        formData.append('invoice_no', editForm.invoiceNo.trim());
      }

      if (editForm.description?.trim()) {
        formData.append('description', editForm.description.trim());
      }

      if (editForm.waybillFile) {
        formData.append('waybill_file', editForm.waybillFile);
      }
      if (editForm.invoiceFile) {
        formData.append('invoice_file', editForm.invoiceFile);
      }

      const res = await api.patch(`/records/concrete/${editForm.id}`, formData);
      const updated = res.data;

      setData((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));

      if (selectedRow?.id === updated.id) {
        setSelectedRow(updated);
      }

      notifications.show({
        title: 'Başarılı',
        message: 'Kayıt güncellendi.',
        color: 'teal',
      });

      closeEditModal();
    } catch (err) {
      let errorMessage = 'Kayıt güncellenemedi.';
      const detail = err?.response?.data?.detail;

      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => `${e.loc?.join('.')} alanı: ${e.msg}`).join(' | ');
      }

      notifications.show({
        title: 'Hata',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    setDeleting(true);
    try {
      await api.delete(`/records/concrete/${deleteTarget.id}`);

      setData((prev) => prev.filter((item) => item.id !== deleteTarget.id));

      if (selectedRow?.id === deleteTarget.id) {
        closeDetailModal();
      }

      notifications.show({
        title: 'Başarılı',
        message: 'Kayıt silindi.',
        color: 'teal',
      });

      closeDeleteModal();
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err?.response?.data?.detail || 'Kayıt silinemedi.',
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const filterOptions = [
    { value: 'ic_santiye', label: 'İç Şantiye' },
    { value: 'dis_satis', label: 'Dış Satış' },
  ];

  const waybillUrl = selectedRow?.waybillPdfUrl || (selectedRow?.waybillPdf ? `/records/concrete/${selectedRow?.id}/waybill-pdf` : null);
  const invoiceUrl = selectedRow?.invoicePdfUrl || (selectedRow?.invoicePdf ? `/records/concrete/${selectedRow?.id}/invoice-pdf` : null);

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: heroGradient,
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          }}
        >
          <Stack gap="md" style={{ position: 'relative', zIndex: 2 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Button
                variant="white"
                radius="xl"
                size="sm"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/beton-takip')}
                style={{
                  background: 'rgba(255,255,255,0.14)',
                  color: 'white',
                  border: 'none',
                }}
              >
                Beton Takip Menüsüne Dön
              </Button>

              <Group gap="xs">
                <Badge variant="light" color="cyan" radius="xl">
                  {totalCount} Kayıt
                </Badge>
                <Badge variant="light" color="teal" radius="xl">
                  {formatVolume(totalVolume)} m³
                </Badge>
              </Group>
            </Group>

            <Group gap="sm" align="center">
              <ThemeIcon size={42} radius="xl" variant="white" color="blue">
                <IconReportAnalytics size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">
                  Araç Filo & Şantiye Yönetimi
                </Text>
                <Title order={1} fw={900} c="white">
                  Beton Takip Raporları
                </Title>
              </Stack>
            </Group>

            <Text c="rgba(255,255,255,0.78)" size="sm" maw={800}>
              Tüm sevkiyatları filtrele, detayını aç, düzenle, sil, PDF dosyalarını indir ve Excel olarak dışa aktar.
            </Text>
          </Stack>
        </Paper>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Paper radius="xl" p="lg" withBorder>
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Text size="sm" c="dimmed">
                  Toplam Kayıt
                </Text>
                <Text fw={900} size={rem(28)}>
                  {totalCount}
                </Text>
              </Stack>
              <ThemeIcon radius="xl" variant="light" color="blue" size={42}>
                <IconClipboardText size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          <Paper radius="xl" p="lg" withBorder>
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Text size="sm" c="dimmed">
                  Toplam Hacim
                </Text>
                <Text fw={900} size={rem(28)}>
                  {formatVolume(totalVolume)} m³
                </Text>
              </Stack>
              <ThemeIcon radius="xl" variant="light" color="teal" size={42}>
                <IconRuler size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          <Paper radius="xl" p="lg" withBorder>
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Text size="sm" c="dimmed">
                  İç / Dış Dağılım
                </Text>
                <Text fw={900} size={rem(18)}>
                  {deliveryCount.internal} / {deliveryCount.external}
                </Text>
              </Stack>
              <ThemeIcon radius="xl" variant="light" color="pink" size={42}>
                <IconTruckDelivery size={20} />
              </ThemeIcon>
            </Group>
          </Paper>
        </SimpleGrid>

        <Paper radius="xl" p={{ base: 'md', md: 'xl' }} withBorder shadow="sm">
          <Stack gap="lg">
            <Group justify="space-between" align="flex-end" wrap="wrap">
              <Stack gap={4} style={{ flex: 1, minWidth: 240 }}>
                <Group gap="xs">
                  <IconFilter size={18} />
                  <Text fw={700}>Filtreler</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  İrsaliye, firma, beton cinsi, teslimat türü, teslim yeri ve tarih aralığına göre süz.
                </Text>
              </Stack>

              <Group gap="sm" wrap="wrap">
                <Button
                  leftSection={<IconX size={16} />}
                  radius="xl"
                  variant="light"
                  color="gray"
                  onClick={clearFilters}
                  disabled={
                    !searchTerm &&
                    !typeFilter &&
                    !concreteFilter &&
                    !locationFilter &&
                    !dateRange?.[0] &&
                    !dateRange?.[1] &&
                    !sortConfig.key
                  }
                >
                  Filtreleri Temizle
                </Button>

                <Tooltip label="Boş veri giriş şablonunu indirir">
                  <Button
                    leftSection={<IconFileText size={17} />}
                    radius="xl"
                    color="gray"
                    variant="light"
                    onClick={handleDownloadTemplate}
                    loading={templateDownloading}
                  >
                    Excel Şablonu
                  </Button>
                </Tooltip>

                <Tooltip label="Filtre ve sıralamayı koruyarak yönetici özeti, grafikler ve detaylı rapor indirir">
                  <Button
                    leftSection={<IconDownload size={18} />}
                    radius="xl"
                    color="teal"
                    variant="filled"
                    onClick={handleExportExcel}
                    loading={exporting}
                    disabled={sortedData.length === 0}
                  >
                    Raporu Excel İndir ({sortedData.length})
                  </Button>
                </Tooltip>
              </Group>
            </Group>

            <SimpleGrid cols={{ base: 1, md: 4 }}>
              <TextInput
                label="Hızlı Arama"
                placeholder="İrsaliye, firma, beton cinsi..."
                leftSection={<IconSearch size={16} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                radius="md"
              />

              <Select
                label="Teslimat Türü"
                placeholder="Tümü"
                data={filterOptions}
                value={typeFilter}
                onChange={setTypeFilter}
                radius="md"
                clearable
              />

              <Select
                label="Beton Cinsi"
                placeholder="Tümü"
                data={concreteTypesFromData}
                value={concreteFilter}
                onChange={(value) => setConcreteFilter(value || '')}
                radius="md"
                clearable
                searchable
              />

              <Select
                label="Teslim Yeri / Firma"
                placeholder="Tümü"
                data={locationTypes}
                value={locationFilter}
                onChange={(value) => setLocationFilter(value || '')}
                radius="md"
                clearable
                searchable
              />
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, md: 1 }}>
              <DatePickerInput
                type="range"
                label="Tarih Aralığı"
                placeholder="Başlangıç - Bitiş"
                value={dateRange}
                onChange={setDateRange}
                valueFormat="DD.MM.YYYY"
                clearable
                allowSingleDateInRange
                radius="md"
              />
            </SimpleGrid>

            <Divider />

            {loading ? (
              <Center py="xl">
                <Loader size="lg" />
              </Center>
            ) : (
              <Box>
                {sortedData.length === 0 ? (
                  <Card withBorder radius="xl" p="xl">
                    <Stack align="center" gap="xs" py="lg">
                      <ThemeIcon size={52} radius="xl" variant="light" color="blue">
                        <IconFileText size={26} />
                      </ThemeIcon>
                      <Text fw={700} size="lg">
                        Kayıt bulunamadı
                      </Text>
                      <Text c="dimmed" ta="center" size="sm">
                        Filtreleri temizleyip tekrar deneyin.
                      </Text>
                    </Stack>
                  </Card>
                ) : (
                  <>
                    <ScrollArea>
                      <Table verticalSpacing="md" highlightOnHover striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <ThSortable sortKey="waybillNo">İrsaliye No</ThSortable>
                            <ThSortable sortKey="date">Tarih</ThSortable>
                            <Table.Th><Text fw={700} size="sm" pl="xs">Cins</Text></Table.Th>
                            <ThSortable sortKey="volume">Hacim</ThSortable>
                            <Table.Th><Text fw={700} size="sm" pl="xs">Tür</Text></Table.Th>
                            <Table.Th><Text fw={700} size="sm" pl="xs">Teslim Yeri / Firma</Text></Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}><Text fw={700} size="sm">İşlem</Text></Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {paginatedData.map((row) => (
                            <Table.Tr key={row.id ?? `${getRowTitle(row)}-${getRowDate(row)}`}>
                              <Table.Td>
                                <Group gap="xs">
                                  <IconFileText size={16} color="gray" />
                                  <Text size="sm" fw={500}>
                                    {getRowTitle(row)}
                                  </Text>
                                </Group>
                              </Table.Td>
                              <Table.Td>{formatDate(getRowDate(row))}</Table.Td>
                              <Table.Td>
                                <Badge variant="dot" color="gray">
                                  {getRowConcrete(row)}
                                </Badge>
                              </Table.Td>
                              <Table.Td fw={700}>{formatVolume(getRowVolume(row))} m³</Table.Td>
                              <Table.Td>
                                <Badge
                                  color={getRowDeliveryType(row) === 'ic_santiye' ? 'blue' : 'pink'}
                                  variant="light"
                                >
                                  {getDeliveryLabel(getRowDeliveryType(row))}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" lineClamp={1}>
                                  {getRowLocation(row)}
                                </Text>
                              </Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Group gap="xs" justify="flex-end">
                                  <Tooltip label="Detayı görüntüle">
                                    <ActionIcon
                                      variant="light"
                                      color="blue"
                                      onClick={() => openDetailModal(row)}
                                    >
                                      <IconEye size={18} />
                                    </ActionIcon>
                                  </Tooltip>

                                  <Tooltip label="Düzenle">
                                    <ActionIcon
                                      variant="light"
                                      color="orange"
                                      onClick={() => openEditModal(row)}
                                    >
                                      <IconPencil size={18} />
                                    </ActionIcon>
                                  </Tooltip>

                                  <Tooltip label="Sil">
                                    <ActionIcon
                                      variant="light"
                                      color="red"
                                      onClick={() => openDeleteModal(row)}
                                    >
                                      <IconTrash size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>

                    <Group justify="space-between" align="center" mt="lg" wrap="wrap">
                      <Group gap="sm" align="center">
                        <Select
                          data={[
                            { value: '10', label: '10 Kayıt' },
                            { value: '25', label: '25 Kayıt' },
                            { value: '50', label: '50 Kayıt' },
                            { value: '100', label: '100 Kayıt' },
                          ]}
                          value={String(pageSize)}
                          onChange={(val) => {
                            setPageSize(Number(val));
                            setPage(1); 
                          }}
                          allowDeselect={false} 
                          size="sm"
                          radius="xl"
                          w={120}
                        />
                        <Text size="sm" c="dimmed">
                          Sayfa {page} / {totalPages} — Toplam {sortedData.length} kayıt
                        </Text>
                      </Group>

                      <Pagination
                        value={page}
                        onChange={setPage}
                        total={totalPages}
                        radius="xl"
                        withEdges
                      />
                    </Group>
                  </>
                )}
              </Box>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Modal
        opened={detailModalOpen}
        onClose={closeDetailModal}
        title="Rapor Detayı"
        radius="xl"
        centered
        size="lg"
      >
        {selectedRow && (
          <Stack gap="md">
            <Paper radius="lg" p="md" withBorder>
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Stack gap={2}>
                  <Text size="sm" c="dimmed">
                    İrsaliye No
                  </Text>
                  <Text fw={800} size="lg">
                    {getRowTitle(selectedRow)}
                  </Text>
                </Stack>

                <Badge
                  size="lg"
                  color={getRowDeliveryType(selectedRow) === 'ic_santiye' ? 'blue' : 'pink'}
                  variant="light"
                >
                  {getDeliveryLabel(getRowDeliveryType(selectedRow))}
                </Badge>
              </Group>
            </Paper>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Paper withBorder radius="lg" p="md">
                <Group gap="sm" align="flex-start">
                  <ThemeIcon variant="light" color="blue" radius="xl">
                    <IconCalendar size={18} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Tarih
                    </Text>
                    <Text fw={600}>{formatDate(getRowDate(selectedRow))}</Text>
                  </Stack>
                </Group>
              </Paper>

              <Paper withBorder radius="lg" p="md">
                <Group gap="sm" align="flex-start">
                  <ThemeIcon variant="light" color="teal" radius="xl">
                    <IconBuildingFactory size={18} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Beton Cinsi
                    </Text>
                    <Text fw={600}>{getRowConcrete(selectedRow)}</Text>
                  </Stack>
                </Group>
              </Paper>

              <Paper withBorder radius="lg" p="md">
                <Group gap="sm" align="flex-start">
                  <ThemeIcon variant="light" color="green" radius="xl">
                    <IconRuler size={18} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Hacim
                    </Text>
                    <Text fw={600}>{formatVolume(getRowVolume(selectedRow))} m³</Text>
                  </Stack>
                </Group>
              </Paper>

              <Paper withBorder radius="lg" p="md">
                <Group gap="sm" align="flex-start">
                  <ThemeIcon variant="light" color="pink" radius="xl">
                    <IconMapPin size={18} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Teslim Yeri / Firma
                    </Text>
                    <Text fw={600}>{getRowLocation(selectedRow)}</Text>
                  </Stack>
                </Group>
              </Paper>

              <Paper withBorder radius="lg" p="md">
                <Group gap="sm" align="flex-start">
                  <ThemeIcon variant="light" color="indigo" radius="xl">
                    <IconDatabase size={18} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Fatura No / Tarih
                    </Text>
                    <Text fw={600}>{getRowInvoiceNo(selectedRow)}</Text>
                    {getRowInvoiceDate(selectedRow) !== '-' && (
                      <Text size="sm" c="dimmed">{formatDate(getRowInvoiceDate(selectedRow))}</Text>
                    )}
                  </Stack>
                </Group>
              </Paper>

              <Paper withBorder radius="lg" p="md">
                <Group gap="sm" align="flex-start">
                  <ThemeIcon variant="light" color="orange" radius="xl">
                    <IconTruckDelivery size={18} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Teslimat Tipi
                    </Text>
                    <Text fw={600}>{getDeliveryLabel(getRowDeliveryType(selectedRow))}</Text>
                  </Stack>
                </Group>
              </Paper>
            </SimpleGrid>

            {selectedRow?.description && (
              <Paper withBorder radius="lg" p="md">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Açıklama
                  </Text>
                  <Text size="sm">{selectedRow.description}</Text>
                </Stack>
              </Paper>
            )}

            <Paper withBorder radius="lg" p="md">
              <Stack gap="10">
                <Text size="xs" c="dimmed" tt="uppercase">
                  PDF Dosyaları
                </Text>

                <Group grow align="stretch">
                  <Button
                    leftSection={<IconFileTypePdf size={16} />}
                    radius="xl"
                    variant="light"
                    onClick={() =>
                      downloadPdf(waybillUrl, `irsaliye_${getRowTitle(selectedRow)}.pdf`)
                    }
                    disabled={!waybillUrl}
                  >
                    İrsaliye PDF İndir
                  </Button>

                  {invoiceUrl ? (
                    <Button
                      leftSection={<IconReceipt2 size={16} />}
                      radius="xl"
                      color="pink"
                      variant="light"
                      onClick={() =>
                        downloadPdf(invoiceUrl, `fatura_${getRowTitle(selectedRow)}.pdf`)
                      }
                      disabled={!invoiceUrl}
                    >
                      Fatura PDF İndir
                    </Button>
                  ) : (
                    <Button radius="xl" variant="light" color="gray" disabled>
                      Fatura Yok
                    </Button>
                  )}
                </Group>

                {!waybillUrl && !invoiceUrl && (
                  <Text size="sm" c="dimmed">
                    Bu kayda ait indirilebilir PDF bulunmuyor.
                  </Text>
                )}
              </Stack>
            </Paper>

            <Divider />

            <Group justify="space-between">
              <Button
                variant="light"
                color="orange"
                radius="xl"
                leftSection={<IconPencil size={16} />}
                onClick={() => {
                  closeDetailModal();
                  openEditModal(selectedRow);
                }}
              >
                Düzenle
              </Button>

              <Button
                variant="light"
                color="red"
                radius="xl"
                leftSection={<IconTrash size={16} />}
                onClick={() => {
                  closeDetailModal();
                  openDeleteModal(selectedRow);
                }}
              >
                Sil
              </Button>

              <Button variant="default" radius="xl" onClick={closeDetailModal}>
                Kapat
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={editModalOpen}
        onClose={closeEditModal}
        title="Raporu Düzenle"
        radius="xl"
        centered
        size="lg"
      >
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="İrsaliye No"
              value={editForm.waybillNo}
              disabled
              radius="md"
            />
            <DatePickerInput
              label="Tarih"
              value={editForm.date}
              onChange={(value) => setEditForm((prev) => ({ ...prev, date: value }))}
              radius="md"
              valueFormat="DD.MM.YYYY"
              clearable
              required
            />

            <NumberInput
              label="Hacim (m³)"
              value={editForm.volume === '' ? '' : Number(editForm.volume)}
              onChange={(value) =>
                setEditForm((prev) => ({
                  ...prev,
                  volume: value === undefined || value === null ? '' : String(value),
                }))
              }
              min={0}
              step={0.01}
              decimalScale={2}
              fixedDecimalScale
              radius="md"
              required
            />

            <Select
              label="Beton Cinsi"
              data={concreteTypesFromData}
              value={editForm.concreteTypeId || ''}
              onChange={(value) =>
                setEditForm((prev) => ({
                  ...prev,
                  concreteTypeId: value || '',
                }))
              }
              radius="md"
              searchable
              clearable
              required
            />
          </SimpleGrid>

          <Paper withBorder radius="lg" p="md">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Teslimat Tipi ve Konum
              </Text>

              <Radio.Group
                value={editForm.deliveryType}
                onChange={(value) =>
                  setEditForm((prev) => ({
                    ...prev,
                    deliveryType: value,
                    siteId: value === 'ic_santiye' ? prev.siteId : '',
                    companyId: value === 'dis_satis' ? prev.companyId : '',
                    // Fatura bilgileri iç şantiyede de kullanılabilir olması için sıfırlamıyoruz.
                  }))
                }
              >
                <Group mt="xs" mb="md">
                  <Radio value="ic_santiye" label="İç Şantiye" />
                  <Radio value="dis_satis" label="Dış Satış" />
                </Group>
              </Radio.Group>
              
              {editForm.deliveryType === 'ic_santiye' ? (
                <Select
                  label="Şantiye"
                  data={sitesFromData}
                  value={editForm.siteId || ''}
                  onChange={(value) => setEditForm((prev) => ({ ...prev, siteId: value || '' }))}
                  radius="md"
                  searchable
                  clearable
                  required
                />
              ) : (
                <Select
                  label="Firma"
                  data={companiesFromData}
                  value={editForm.companyId || ''}
                  onChange={(value) => setEditForm((prev) => ({ ...prev, companyId: value || '' }))}
                  radius="md"
                  searchable
                  clearable
                  required
                />
              )}
            </Stack>
          </Paper>

          <Paper withBorder radius="lg" p="md">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Fatura ve Açıklama Bilgileri
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Fatura No"
                  value={editForm.invoiceNo}
                  onChange={(e) => {
                    const val = e.currentTarget.value;
                    setEditForm((prev) => ({ ...prev, invoiceNo: val }));
                  }}
                  radius="md"
                />

                <DatePickerInput
                  label="Fatura Tarihi"
                  value={editForm.invoiceDate}
                  onChange={(value) => setEditForm((prev) => ({ ...prev, invoiceDate: value }))}
                  radius="md"
                  valueFormat="DD.MM.YYYY"
                  clearable
                />
              </SimpleGrid>

              <Textarea
                label="Açıklama"
                value={editForm.description}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  setEditForm((prev) => ({ ...prev, description: val }));
                }}
                minRows={3}
                radius="md"
                autosize
              />
            </Stack>
          </Paper>

          <Paper withBorder radius="lg" p="md">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                PDF Yüklemeleri
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <FileInput
                  label="İrsaliye PDF"
                  description={selectedRow?.waybillPdf ? "Durum: Mevcut İrsaliye Yüklü" : "Durum: Yüklü Dosya Yok"}
                  placeholder="Dosyayı Değiştir / Yükle"
                  accept="application/pdf"
                  leftSection={<IconUpload size={16} />}
                  value={editForm.waybillFile}
                  onChange={(file) => setEditForm((prev) => ({ ...prev, waybillFile: file }))}
                  radius="md"
                  clearable
                />
                <FileInput
                  label="Fatura PDF"
                  description={selectedRow?.invoicePdf ? "Durum: Mevcut Fatura Yüklü" : "Durum: Yüklü Dosya Yok"}
                  placeholder="Dosyayı Değiştir / Yükle"
                  accept="application/pdf"
                  leftSection={<IconUpload size={16} />}
                  value={editForm.invoiceFile}
                  onChange={(file) => setEditForm((prev) => ({ ...prev, invoiceFile: file }))}
                  radius="md"
                  clearable
                />
              </SimpleGrid>
            </Stack>
          </Paper>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" radius="xl" onClick={closeEditModal}>
              Vazgeç
            </Button>

            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              radius="xl"
              onClick={handleEditSubmit}
              loading={saving}
            >
              Kaydet
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteModalOpen}
        onClose={closeDeleteModal}
        title="Kaydı Sil"
        radius="xl"
        centered
        size="md"
      >
        <Stack gap="md">
          <Text>
            <strong>{getRowTitle(deleteTarget)}</strong> numaralı irsaliyeyi silmek üzeresin. Bu işlem geri alınamaz.
          </Text>

          <Group justify="flex-end">
            <Button variant="default" radius="xl" onClick={closeDeleteModal}>
              İptal
            </Button>
            <Button color="red" radius="xl" leftSection={<IconTrash size={16} />} loading={deleting} onClick={handleDelete}>
              Sil
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}